
import { Component, Vue, Watch } from 'vue-property-decorator'
import faqData from '@/data/faq.json'
import { AppModule } from '@/store/module'
import { FAQ_SCENES } from '@/types/faq'
import type { FaqCategory as FaqCategoryType, FaqData } from '@/types/faq'
import { filterFaqGroupsByScene, toFaqCategories } from '@/utils/faq'
import HeroBanner from './components/HeroBanner.vue'
import CategorySidebar from './components/CategorySidebar.vue'
import CategoryTags from './components/CategoryTags.vue'
import FaqCategory from './components/FaqCategory.vue'
import ContactBlock from '@/components/ContactBlock.vue'
import HelpBlock from '@/components/HelpBlock.vue'
import FooterBlock from '@/components/FooterBlock.vue'
import Breadcrumb from '@/components/Breadcrumb.vue'

/**
 * FaqPage main component
 * 
 * Displays FAQ content with responsive layout:
 * - PC (>650px): Two-column layout with sidebar navigation
 * - H5 (≤650px): Stacked layout with fixed category tags
 * 
 * Loads unified FAQ data and transforms it to the category array.
 * Implements v-if conditional rendering for PC/H5 layouts.
 * Implements scroll spy for category navigation.
 * 
 * @see Requirements 2.1, 3.1, 4.2, 4.4, 4.5, 5.1, 5.2, 5.3, 5.4, 5.5, 5.6, 8.8, 9.2, 9.3, 9.5
 */
@Component({
  components: {
    HeroBanner,
    CategorySidebar,
    CategoryTags,
    FaqCategory,
    ContactBlock,
    HelpBlock,
    FooterBlock,
    Breadcrumb,
  },
})
export default class FaqPage extends Vue {
  // === Reactive Data ===

  /** Breadcrumb navigation items */
  breadcrumbItems = [{ label: '首頁', to: '/' }, { label: '常見問題' }]

  /** FAQ categories loaded from the unified data source */
  private categories: FaqCategoryType[] = []

  /** Currently active/highlighted category ID */
  private activeCategoryId = ''

  /** Currently active/highlighted item ID */
  private activeItemId = ''

  /** Height of the sticky navigation bar */
  private readonly navHeight: number = 50

  /** Flag to prevent scroll spy from overriding user click scroll */
  private isScrollingToCategory = false

  /** Timer for scroll end detection */
  private scrollEndTimer: number | null = null

  /** requestAnimationFrame id for throttled scroll spy updates */
  private scrollSpyFrame: number | null = null

  // === Computed Properties ===

  /**
   * Check if current viewport is H5 (mobile)
   * Uses Vuex AppModule isH5 state
   */
  get isH5(): boolean {
    return AppModule.isH5
  }

  // === Lifecycle Hooks ===

  /**
   * Load FAQ data on component creation
   */
  created(): void {
    this.loadFaqData()
    this.updateResponsiveState()
  }

  /**
   * Initialize responsive state and scroll spy on mount
   */
  mounted(): void {
    window.addEventListener('resize', this.handleResize)
    // Initialize scroll spy after DOM is ready
    this.$nextTick(() => {
      this.initScrollSpy()
      this.handleRouteHash()
    })
  }

  /**
   * Clean up event listeners
   */
  beforeDestroy(): void {
    window.removeEventListener('resize', this.handleResize)
    this.destroyScrollSpy()
  }

  // === Watchers ===

  /**
   * Watch for isH5 changes to switch scroll spy offsets
   * @see Requirement 5.6
   */
  @Watch('isH5')
  onIsH5Change(): void {
    // Re-initialize scroll spy when viewport mode changes
    this.$nextTick(() => {
      this.destroyScrollSpy()
      this.initScrollSpy()
    })
  }

  @Watch('$route.hash')
  onRouteHashChange(): void {
    this.$nextTick(() => {
      this.handleRouteHash()
    })
  }

  // === Methods ===

  /**
   * Load FAQ data from the unified source for the FAQ scene
   * Transforms unified groups to the internal FaqCategory array
   * 
   * @see Requirement 8.8
   */
  private loadFaqData(): void {
    try {
      const faqGroups = filterFaqGroupsByScene(
        faqData as unknown as FaqData,
        FAQ_SCENES.FAQ,
      )

      this.categories = [...toFaqCategories(faqGroups)]

      if (this.categories.length > 0) {
        this.activeCategoryId = this.categories[0].id
      }
    } catch (error) {
      console.error('Failed to load FAQ data:', error)
      this.categories = []
    }
  }

  /**
   * Update responsive state based on viewport width
   * Updates Vuex AppModule isH5 state
   * 
   * @see Requirement 9.1
   */
  private updateResponsiveState(): void {
    const isNowH5 = window.innerWidth <= 650
    if (AppModule.isH5 !== isNowH5) {
      AppModule.setIsH5(isNowH5)
    }
  }

  /**
   * Handle window resize event
   * Updates responsive state when viewport changes
   * 
   * @see Requirement 9.2, 9.3
   */
  private handleResize(): void {
    this.updateResponsiveState()
  }

  /**
   * Initialize scroll spy.
   *
   * Highlights the latest category title that has crossed the fixed header
   * offset. This is more stable than observing whole FAQ sections because
   * FAQ sections can be much taller than the viewport trigger band.
   *
   * @see Requirements 4.2, 4.4, 4.5, 5.1, 5.2, 5.3, 5.4, 5.5, 5.6
   */
  private initScrollSpy(): void {
    window.addEventListener('scroll', this.handleScrollSpy, { passive: true })
    this.updateActiveStateByScroll()
  }

  private handleScrollSpy = (): void => {
    if (this.isScrollingToCategory || this.scrollSpyFrame !== null) return

    this.scrollSpyFrame = window.requestAnimationFrame(() => {
      this.scrollSpyFrame = null
      this.updateActiveStateByScroll()
    })
  }

  private getScrollSpyOffset(): number {
    if (!this.isH5) return this.navHeight + 16

    const tagsEl = document.querySelector('.category-tags') as HTMLElement | null
    const tagsHeight = tagsEl ? tagsEl.offsetHeight : 52
    return this.navHeight + tagsHeight + 8
  }

  private updateActiveStateByScroll(): void {
    const categoryAnchors = Array.from(document.querySelectorAll('.faq-category-anchor[data-category-id]')) as HTMLElement[]
    if (categoryAnchors.length === 0) return

    const targetY = window.scrollY + this.getScrollSpyOffset()
    const boundaryTolerance = 1
    let activeCategoryAnchor = categoryAnchors[0]
    let activeCategoryId = activeCategoryAnchor.getAttribute('data-category-id') || ''
    let activeItemId = ''

    categoryAnchors.forEach(el => {
      const categoryId = el.getAttribute('data-category-id')
      if (!categoryId) return

      const elementTop = el.getBoundingClientRect().top + window.scrollY
      if (elementTop <= targetY + boundaryTolerance) {
        activeCategoryAnchor = el
        activeCategoryId = categoryId
      }
    })

    const activeCategory = activeCategoryAnchor.closest('.faq-category')
    const itemCards = activeCategory
      ? Array.from(activeCategory.querySelectorAll('.faq-card[data-item-id]')) as HTMLElement[]
      : []

    itemCards.forEach(el => {
      const itemId = el.getAttribute('data-item-id')
      if (!itemId) return

      const elementTop = el.getBoundingClientRect().top + window.scrollY
      if (elementTop <= targetY + boundaryTolerance) {
        activeItemId = itemId
      }
    })

    if (activeCategoryId && activeCategoryId !== this.activeCategoryId) {
      this.activeCategoryId = activeCategoryId
    }

    if (activeItemId !== this.activeItemId) {
      this.activeItemId = activeItemId
    }
  }

  /**
   * Destroy scroll spy listeners
   */
  private destroyScrollSpy(): void {
    window.removeEventListener('scroll', this.handleScrollSpy)
    if (this.scrollSpyFrame !== null) {
      window.cancelAnimationFrame(this.scrollSpyFrame)
      this.scrollSpyFrame = null
    }
  }

  /**
   * Handle category click from sidebar or tags
   * Updates active category and triggers scroll
   */
  private handleCategoryClick(categoryId: string): void {
    this.activeCategoryId = categoryId
    this.activeItemId = ''
    this.scrollToCategory(categoryId)
  }

  /**
   * Handle question click from sidebar
   * Updates active item and scrolls to the specific question card
   */
  private handleQuestionClick(itemId: string): void {
    // Find which category this item belongs to and update active category
    const category = this.categories.find(c => c.items.some(item => item.id === itemId))
    if (category) {
      this.activeCategoryId = category.id
    }
    this.activeItemId = itemId
    this.scrollToItem(itemId)
  }

  private getCategoryIdFromHash(): string {
    const hash = this.$route.hash || ''
    if (!hash) return ''

    try {
      return decodeURIComponent(hash.replace(/^#/, ''))
    } catch (error) {
      return hash.replace(/^#/, '')
    }
  }

  private handleRouteHash(): void {
    const categoryId = this.getCategoryIdFromHash()
    if (!categoryId) return

    const category = this.categories.find(item => item.id === categoryId)
    if (!category) return

    this.activeCategoryId = categoryId
    this.activeItemId = ''
    window.setTimeout(() => {
      this.scrollToCategory(categoryId)
    }, 0)
  }

  /**
   * Scroll to a specific question card by item ID
   */
  private scrollToItem(itemId: string): void {
    const element = document.querySelector(`.faq-card[data-item-id="${itemId}"]`)
    if (!element) return

    this.isScrollingToCategory = true

    const elementTop = element.getBoundingClientRect().top + window.scrollY
    window.scrollTo({
      top: elementTop - this.navHeight - 16,
      behavior: 'smooth'
    })

    if (this.scrollEndTimer !== null) clearTimeout(this.scrollEndTimer)
    this.scrollEndTimer = window.setTimeout(() => {
      this.isScrollingToCategory = false
      this.updateActiveStateByScroll()
    }, 800)
  }

  /**
   * Scroll to the specified category with smooth animation
   * PC: Uses scrollIntoView with smooth behavior
   * H5: Accounts for fixed tags bar height offset
   * 
   * @param categoryId - The ID of the category to scroll to
   */
  private scrollToCategory(categoryId: string): void {
    const element = document.querySelector(`.faq-category[data-category-id="${categoryId}"]`)
    if (!element) return

    this.isScrollingToCategory = true

    if (this.isH5) {
      const tagsEl = document.querySelector('.category-tags') as HTMLElement | null
      const tagsHeight = tagsEl ? tagsEl.offsetHeight : 0
      const elementTop = element.getBoundingClientRect().top + window.scrollY
      const scrollTarget = elementTop - this.navHeight - tagsHeight - 8
      window.scrollTo({ top: scrollTarget, behavior: 'smooth' })
    } else {
      const elementTop = element.getBoundingClientRect().top + window.scrollY
      window.scrollTo({ top: elementTop - this.navHeight - 16, behavior: 'smooth' })
    }

    if (this.scrollEndTimer !== null) clearTimeout(this.scrollEndTimer)
    const animationDuration = this.isH5 ? 500 : 800
    this.scrollEndTimer = window.setTimeout(() => {
      this.isScrollingToCategory = false
      this.updateActiveStateByScroll()
    }, animationDuration)
  }
}
